import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  lang?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = "/img/img_hiddenai.jpeg",
  url,
  type = "website",
  lang,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = lang || i18n.language;

  // 기본 SEO 데이터 (언어별) - 창작소 컨셉으로 업데이트
  const defaultSEO = {
    ko: {
      title: "Lingple",
      description:
        "다양한 멀티플레이 게임과 오프라인 이벤트 도구들을 통해 모임을 더욱 알차게 만들어보세요.",
      keywords:
        "플랫폼, 레크레이션, 모임, 행사, 게임, 도구, 멀티플레이, 오프라인, 역할 분배, 마피아, 팀 빌딩, 숫자 추첨, 워크숍",
    },
    en: {
      title: "Lingple",
      description:
        "Make your meetings more meaningful with various multiplayer games and offline event tools.",
      keywords:
        "platform, recreation, meetings, events, games, tools, multiplayer, offline, role distribution, mafia, team builder, lucky draw, workshop",
    },
  };

  const seoData =
    defaultSEO[currentLang as keyof typeof defaultSEO] || defaultSEO.ko;

  const finalTitle = title || seoData.title;
  const finalDescription = description || seoData.description;
  const finalKeywords = keywords || seoData.keywords;

  // URL 생성 (안전하게)
  const getCanonicalUrl = (): string => {
    if (url) {
      return url.split("?")[0]; // 쿼리 파라미터 제거
    }

    // 기본 URL 생성
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }

    return "https://your-domain.com"; // 서버 사이드 렌더링 시 기본값
  };

  const canonicalUrl = getCanonicalUrl();

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta
        property="og:locale"
        content={currentLang === "ko" ? "ko_KR" : "en_US"}
      />
      <meta property="og:site_name" content="Lingple" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      <html lang={currentLang} />
    </Helmet>
  );
};

export default SEO;
